import { useRef, useState } from "react";
import { darken, RIF, setAlpha, useClickOutside } from "../../lib";
import { useSelectTheme } from "../../store";
import { TempTask, getTaskContentName } from "../../model";
import { convertMovesenseDescription, convertGarminStreamDescription, ButtonMore } from "..";
import {
  TodoBlackIcon,
  SurveyBlackIcon,
  TimerBlackIcon,
  RepeatIcon,
  DeviceIcon,
} from "../../asset/image";
import MovesenseImg from "../../asset/image/movesense.png";
import { differenceInMilliseconds } from "date-fns";
import { TaskScheduleCard } from "../molecules/task_schedule_card";
import { IStopwatchGarminStream, IStopwatchMovesenseStream, TaskType } from "../../shared/db";
import { isNil, omit } from "lodash";

export interface HiddenTaskTagPropsT {
  task: TempTask;
  key?: string;
  onEditing: () => void;
  onUnarchive: (task: TempTask) => void;
}

export const HiddenTaskTag = (props: HiddenTaskTagPropsT) => {
  const { color, pad, fontWeight, fontSize } = useSelectTheme();

  const { task, onEditing, onUnarchive } = props;
  const taskContentName = getTaskContentName(task.type);
  const isEventTrackerTask = !isNil(task.eventTracker);

  const sortedScheduleList = [...(task.scheduleList ?? [])].sort((a, b) => {
    return differenceInMilliseconds(new Date(b.createdAt), new Date(a.createdAt));
  });

  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => setHovered(true);
  const handleMouseLeave = () => setHovered(false);

  const handleClick = (e: any) => {
    if (e.target.tagName !== "BUTTON") onEditing();
  };

  const [showingDropdown, setShowingDropdown] = useState(false);
  const brickRef = useRef<HTMLInputElement | null>(null);
  useClickOutside(brickRef, () => setShowingDropdown(false));

  const handleClickMoreBtn = () => {
    setShowingDropdown(true);
  };

  const handleUnarchiveBtnClick = () => {
    onUnarchive(task);
  };

  const taskColor = task?.color ? `#${task.color}` : color.primary;

  const taskTagStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    borderRadius: "5px",
    border: `1px solid ${hovered ? color.grey_400 : color.grey_160}`,
    borderLeft: `6px solid ${taskColor}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: `${pad.medium} ${pad.mediumSmall}`,
    gap: "8px",
    background: hovered ? color.grey_20 : color.white,
    cursor: "pointer",
  };

  const eventTrackerTaskTagStyle: React.CSSProperties = {
    ...omit(taskTagStyle, "borderLeft", "border", "background"),
    border: `1px solid ${hovered ? darken(taskColor, 10) : taskColor}`,
    background: setAlpha(taskColor, hovered ? 0.18 : 0.08),
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      css={{
        width: "100%",
        height: "fit-content",
        position: "relative",
        marginBottom: pad.mediumSmall,
        display: "flex",
      }}
    >
      <a
        onClick={handleClick}
        css={{
          ...(isEventTrackerTask ? eventTrackerTaskTagStyle : taskTagStyle),
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            width: "100%",
            height: "100%",
            gap: "8px",
          }}
        >
          <div
            css={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
              }}
            >
              {RIF(task.type === TaskType.Todo, <img src={TodoBlackIcon} width="24" height="24" />)}
              {RIF(
                task.type === TaskType.Timer,
                <img src={TimerBlackIcon} width="24" height="24" />,
              )}
              {RIF(
                task.type === TaskType.Questionnaire,
                <img src={SurveyBlackIcon} width="24" height="24" />,
              )}
              {RIF(
                task.type === TaskType.StopwatchMovesenseStream ||
                  task.type === TaskType.StopwatchMovesenseLogData,
                <img src={MovesenseImg} width="24" height="24" />,
              )}
              {RIF(
                task.type === TaskType.StopwatchGarminStream,
                <img src={DeviceIcon} width="22" height="22" />,
              )}
              {RIF(task[taskContentName]?.name, (name) => (
                <span css={{ fontWeight: fontWeight.thick, fontSize: fontSize.h6 }}>{name}</span>
              ))}
            </div>
            <ButtonMore data-testid="task_tag_more_btn" onClick={handleClickMoreBtn} />
          </div>
          {RIF(
            task.scheduleRepeat || task.scheduleDescription,
            <div css={{ display: "flex", alignItems: "center", gap: "3px" }}>
              {RIF(task.scheduleRepeat, <img src={RepeatIcon} width="14" height="14" />)}
              {RIF(task.scheduleDescription, (description) => (
                <p css={{ color: color.grey_400, fontSize: fontSize.h7 }}>{description}</p>
              ))}
            </div>,
          )}
          {RIF(
            task.type === TaskType.StopwatchMovesenseStream ||
              task.type === TaskType.StopwatchMovesenseLogData ||
              task.type === TaskType.StopwatchGarminStream,
            <div
              css={{
                background: color.background,
                borderRadius: "8px",
                padding: "8px 12px",
                color: color.grey_600,
                fontSize: "12px",
              }}
            >
              {RIF(
                task.type === TaskType.StopwatchMovesenseStream ||
                  task.type === TaskType.StopwatchMovesenseLogData,
                convertMovesenseDescription(task[taskContentName] as IStopwatchMovesenseStream),
              )}
              {RIF(
                task.type === TaskType.StopwatchGarminStream,
                convertGarminStreamDescription(task[taskContentName] as IStopwatchGarminStream),
              )}
            </div>,
          )}
          {RIF(
            sortedScheduleList.length,
            <div css={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
              {sortedScheduleList
                .filter((schedule) => schedule.current)
                .map((schedule) => (
                  <TaskScheduleCard
                    key={schedule.id}
                    schedule={schedule}
                    isViewOnly={true}
                    style={{
                      border: `1px solid ${color.border.default}`,
                      borderRadius: "5px",
                    }}
                  />
                ))}
            </div>,
          )}
        </div>
      </a>

      <div
        ref={brickRef}
        css={{
          position: "relative",
          width: "1.71rem",
          height: "1.71rem",
        }}
      >
        {RIF(
          showingDropdown,
          <div
            css={{
              width: "100px",
              borderRadius: "5px",
              boxShadow: "0px 4px 10px 1px rgba(104, 104, 104, 0.25)",
              border: `1px solid ${color.border.disabled}`,
              backgroundColor: color.white,
              zIndex: "99",
              position: "absolute",
              top: "1.68rem",
              right: "1rem",
              padding: "8px 0",
            }}
          >
            <button
              data-testid="task_tag_remove_btn"
              onClick={handleUnarchiveBtnClick}
              css={{
                width: "100%",
                height: "28px",
                padding: "5px 16px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                fontWeight: fontWeight.regular,
                textAlign: "start",
                ":hover": {
                  backgroundColor: color.hover,
                },
                ":focus": { outline: "none" },
              }}
            >
              Unarchived
            </button>
          </div>,
        )}
      </div>
    </div>
  );
};
